import React, { useContext, useMemo } from 'react';

import { Close } from '@mui/icons-material';
import { Alert, IconButton } from '@mui/material';
import classnames from 'classnames';

import { ALERT_VARIANTS, useAlerts } from '@ezbobdev/ui-commons';
import { MainContext, useTranslation } from '@ezbobdev/ui-framework';
import {
  MaterialButton,
  MaterialDialog,
  MaterialDialogActions,
  MaterialDialogContent,
  MaterialDialogTitle,
  MaterialTypography,
} from '@ezbobdev/ui-widgets-ezbob';

import { styled } from '../../Utils/style';

const Wrapper = styled('div')`
  position: sticky;
  top: 0;
  z-index: 1300;
`;

const StyledList = styled('ul')`
  text-align: initial;
  align-self: center;
  padding-inline-start: 24px;
  margin: 0;
`;

const MaterialAlerts = (props) => {
  const { onClose, className, alerts, configByName, alertVariant, closeAlertByType } = useAlerts(
    { ...props, name: props.name || 'materialAlerts' },
    MaterialAlerts.displayName,
  );

  const { t } = useTranslation();
  const { actions } = useContext(MainContext);

  return useMemo(() => {
    if (!alerts.length) return;

    if (alertVariant === ALERT_VARIANTS.popup) {
      return alerts.map((alert, i) => {
        const { type } = alert;
        const messagesFilteredByType = alerts.filter((item) => item.type === type).map((item) => item.message);

        return (
          <MaterialDialog
            key={`${type}-${i}`}
            open={!!alerts.length}
            variant="small"
            PaperProps={{ 'aria-labelledby': undefined, 'aria-label': t(`${configByName}.${type}.ariaLabel`) }}
          >
            <MaterialDialogTitle
              disableTypography
              icon={
                <img
                  alt={t(`${configByName}.${type}.iconAlt`)}
                  className="dialogTitleIcon"
                  src={actions.settings.getUserConfigProp(`widgetsConfig.${configByName}.${type}.icon`)}
                />
              }
              withDivider={false}
              onClose={() => closeAlertByType(type)}
            >
              <MaterialTypography variant="h2">{t(`${configByName}.${type}.title`)}</MaterialTypography>
            </MaterialDialogTitle>
            <MaterialDialogContent>
              {messagesFilteredByType.length === 1 ? (
                <MaterialTypography variant="body1">{messagesFilteredByType[0]}</MaterialTypography>
              ) : (
                <StyledList>
                  {messagesFilteredByType.map((message, i) => (
                    <li key={`${message}-${i}`}>
                      <MaterialTypography variant="body1">{message}</MaterialTypography>
                    </li>
                  ))}
                </StyledList>
              )}
            </MaterialDialogContent>
            <MaterialDialogActions>
              <MaterialButton fullWidth onClick={() => closeAlertByType(type)}>
                {t(`${configByName}.${type}.closeButtonText`)}
              </MaterialButton>
            </MaterialDialogActions>
          </MaterialDialog>
        );
      });
    }

    return (
      <Wrapper className={classnames(MaterialAlerts.displayName, className)} aria-live="polite">
        {alerts.map(({ message, type }, index) => (
          <Alert
            key={index}
            severity={type}
            role=""
            action={
              <IconButton
                aria-label={t(`globals.popupClose`)}
                color="inherit"
                size="small"
                onClick={() => onClose(index)}
              >
                <Close fontSize="inherit" />
              </IconButton>
            }
          >
            <MaterialTypography role="alert" dangerouslySetInnerHTML={{ __html: message }} />
          </Alert>
        ))}
      </Wrapper>
    );
  }, [className, alerts, alertVariant, closeAlertByType]);
};

MaterialAlerts.displayName = `MaterialAlerts`;

export default MaterialAlerts;
